import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { Refresh } from "@material-ui/icons";

import CheckIcon from '@material-ui/icons/Check';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import DeleteIcon from "@material-ui/icons/Delete";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
} from "@material-ui/core";

import { subtractObject } from "../../../core/utils";
import { getData, postData } from "../../../core/fetchService";
import { DIALOG_USER_STATE } from "../../../core/constants";

import { CheckSampleContent } from "../../components/checkSample";
import TextField from "../../components/material-ui/TextField";
import CloseBar from "../../components/dialogCloseBar";
import ConfirmDialog from "../../components/confirmDialog";
import notBackdropClicked from "../../components/helpers/notBackdropClicked";
import EnhancedTable from "../../components/projectTable";
import IconButton from "../../components/material-ui/IconButton";
import { EditConfig } from "../../components/editConfig";

import { BackendSettings } from "../dashboard/SettingsDialog";


const EditDialog = (props) => {
  const { rowId, isClone, classes, backendList, onClose, onSave } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const backend = (backendList || []).find((s) => s._id == rowId) || {};
  const { name, description, url, adminUrl, type, maxModels } = backend;

  const initData = { name, description, url, adminUrl, type };

  const [isRuntime, setIsRuntime] = useState(backend.type == "runtime");
  const [typeOfBackend, setTypeOfBackend] = useState(rowId ? backend.type : "");

  const [backendData, setBackendData] = useState(initData);
  const [dashboardModels, setDashboardModels] = useState([]);
  const [detailsContent, setDetailsContent] = useState("");
  const [currentModelParams, setCurrentModelParams] = useState({});

  const [isShowDashBoard, setIsShowDashBoard] = useState(false);
  const [isFetchingModels, setIsFetchingModels] = useState(false);
  const [isShowCheckSample, setIsShowCheckSample] = useState(false);
  const [isShowConfirmDialog, setIsShowConfirmDialog] = useState(false);
  const [isShowDetails, setIsShowDetails] = useState(false);

  const initModelState = (runtime_name, type) => {
    setCurrentModelParams({
      name: runtime_name,
      runtime_name,
      type: type == "rasa" ? "NLU_RASA" : "NLU_NB",
      nlu_train_url: backendData.url,
      is_external: false,
    });
  }

  const fetchModels = (isShowLoader = true) => {
    isShowLoader && setIsFetchingModels(true);
    const urlM = isRuntime
      ? `/api/runtime/list_models?server=${backendData.adminUrl}&type=NLU_RASA&is_mgmt=true&showDetails=true`
      : `/api/settings/similarity_config_details?server=${backendData.url}`;
    getData(
      urlM,
      dispatch,
      (data) => {
        try {
          if (!isRuntime) {
            const mapData = Object.entries(
              data.similarity_config_details.models
            ).map((item, index) => ({
              _id: index,
              name: item[0],
              type: item[1].type,
              task: item[1].task,
            }));

            setDashboardModels(mapData);
          } else {
            const mapData = data.map((x, index) => ({
              _id: index,
              ...x,
            }));

            setDashboardModels(mapData);
          }
        } finally {
          setIsFetchingModels(false);
        }
      },
      {
        on_error: () => {
          setIsShowDashBoard(false);
        },
      }
    );
  };

  const fetchDetails = (name) => {
    setIsShowDetails(true);
    const urlD = `/api/backend/model/details?server=${backendData.adminUrl}&name=${name}&type=${typeOfBackend}`;

    getData(urlD, dispatch, (data) => {
      setDetailsContent(JSON.stringify(data[0], null, 2));
    });
  };

  useEffect(() => {
    if (isShowDashBoard) {
      fetchModels();
    }
  }, [isShowDashBoard]);

  const handleCancel = () => onClose();

  const handleTypeOfBackendChange = (e) => {
    const { value } = e.target;

    setTypeOfBackend(value);
    setBackendData((prevState) => ({ ...prevState, type: value }));
    setIsRuntime(value == "runtime");
  };

  const handleUrlChange = (e, key) => {
    const value = e.target?.value;
    setBackendData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleUpdateModel = (name, type) => func => {
    initModelState(name, type);
    func();
  }

  const handleCheckSampleModalOpen = () => {
    setIsShowCheckSample(true);
  };
  
  const handleConfirmModalOpen = () => {
    setIsShowConfirmDialog(true);
  };

  const handleUnloadModel = () => {
    const query = new URLSearchParams();
    query.append('server', backendData.url);
    query.append('name', currentModelParams.runtime_name);
    query.append('typeOfServer', backendData.type);
    query.append('type', currentModelParams.type == "rasa" ? "NLU_RASA" : "NLU_NB");

    postData(`/api/backend/model/unload?${query}`, {}, dispatch, () => {
      setDashboardModels(prevState => prevState.filter(i => i.name !== currentModelParams.name))
    });
  };

  const handleConfirmModalClose = (status) => {
    if (status == DIALOG_USER_STATE.AGREE) {
      handleUnloadModel();
    }
    setIsShowConfirmDialog(false);
  }

  const scroll = "paper";
  const isDisableButton = ![
    "name",
    "url",
    "type",
    ...(isRuntime ? ["adminUrl"] : []),
  ].every((k) => backendData[k]);

  const headCells = [
    { _id: "name", label: t("backends.model"), textSearch: true },
    { _id: "type", label: t("backends.type"), width: "15%", textSearch: true },
    ...(isRuntime
      ? [{ _id: "task", label: t("backends.task"), width: "15%", textSearch: true }]
      : []),
  ];

  return (
    <Dialog
      PaperProps={{
        style: {
          minWidth: "800px",
        },
      }}
      fullWidth={true}
      open={true}
      onClose={notBackdropClicked(handleCancel)}
      scroll={scroll}
    >
      {isShowCheckSample && (
        <ConfirmDialog
          title={t("backends.check_sample", {
            name: currentModelParams.name,
          })}
          content={
            <CheckSampleContent
              classes={classes}
              modelParams={currentModelParams}
              options={{
                is_ext_asr: false,
                is_ext_nb: false,
                is_nb: true,
                server_url: backendData.url,
              }}
            />
          }
          showBtnNameAgree={false}
          btnNameDisagree={t("common.close")}
          closeModal={() => setIsShowCheckSample(false)}
          maxWidth="md"
        />
      )}
      {isShowConfirmDialog &&
          <ConfirmDialog
            closeModal={handleConfirmModalClose}
            btnNameAgree={t("common.ok")}
            btnNameDisagree={t("common.cancel")}
            title={t("backends.unload")}
            content={`${t("backends.delete_backend")}: "${
              currentModelParams.runtime_name
            }"?`}
          />
        }
      <EditConfig
        isOpen={isShowDetails}
        onClose={() => setIsShowDetails(false)}
        nluConfigText={detailsContent}
        mode="json"
        title={t("backends.model_details")}
      />
      <CloseBar
        onClose={handleCancel}
        title={
          rowId
            ? isClone
              ? t("backends.clone_backend")
              : t("backends.edit_backend")
            : t("backends.add_backend")
        }
      />
      <DialogContent dividers={scroll == "paper"}>
        <TextField
          id="backendName"
          required
          size="small"
          className={classes.dialogField}
          label={t("common.name")}
          onChange={(event) => {
            const value = event.target?.value;
            setBackendData((prevState) => ({
              ...prevState,
              name: value,
            }));
          }}
          variant="outlined"
          fullWidth
          defaultValue={name || null}
        />
        <TextField
          id="backendDescription"
          size="small"
          className={classes.dialogField}
          label={t("common.description")}
          onChange={(event) => {
            const value = event.target?.value;
            setBackendData((prevState) => ({
              ...prevState,
              description: value,
            }));
          }}
          variant="outlined"
          multiline
          rows={2}
          fullWidth
          defaultValue={description || null}
        />
        <FormControl variant="outlined" className={classes.selectType}>
          <InputLabel id="backendType">{t("backends.type_of")}</InputLabel>
          <Select
            className={classes.selectField}
            labelId="backendType"
            id="backendTypeSelect"
            value={typeOfBackend}
            disabled={!!rowId}
            onChange={handleTypeOfBackendChange}
            label="Type of backend"
          >
            <MenuItem value="training">{t("backends.training")}</MenuItem>
            <MenuItem value="runtime">{t("backends.runtime")}</MenuItem>
          </Select>
        </FormControl>

        <BackendSettings
          fields={["url"]}
          settings={backendData}
          setSettings={() => (event) => {
            handleUrlChange(event, "url");
          }}
          fieldDescr={{
            url: {
              type: typeOfBackend == "training" ? "nb_server" : "nb_runtime",
              props: {
                id: "url",
                required: true,
                label: t("backends.url"),
              },
              info: typeOfBackend == "training",
              classes_info: classes.dialogField,
            },
          }}
        />
        {isRuntime && (
          <>
            <BackendSettings
            fields={["adminUrl"]}
            settings={backendData}
            setSettings={() => (event) => {
              handleUrlChange(event, "adminUrl");
            }}
            fieldDescr={{
              adminUrl: {
                type: "nb_runtime",
                props: {
                  id: "adminUrl",
                  required: true,
                  label: t("backends.admin_url"),
                },
                info: true,
                classes_info: classes.dialogField,
              },
            }}
            />
            <TextField
              id="maxModels"
              size="small"
              className={classes.dialogField}
              style={{maxWidth: '30%'}}
              label={t("backends.max_models")}
              onChange={(event) => {
                const value = event.target?.value;
                setBackendData((prevState) => ({
                  ...prevState,
                  maxModels: value,
                }));
              }}
              autoComplete="off"
              type="number"
              variant="outlined"
              fullWidth
              defaultValue={maxModels || null}
            />
          </>
        )}
        <div className={classes.dasboardWrapper}>
          <FormControlLabel
            style={{ margin: 8, height: "auto" }}
            control={<Checkbox />}
            disabled={![
              "type",
              ...(isRuntime ? ["adminUrl"] : ["url"])
            ].every((k) => backendData[k])}
            label={t("backends.show_dashboard")}
            checked={isShowDashBoard}
            onChange={() => {
              setIsShowDashBoard((prevState) => !prevState);
            }}
          />
          {isShowDashBoard && (
            <IconButton
              title={t("common.refresh")}
              onClick={() => fetchModels(false)}
              Icon={Refresh}
            />
          )}
        </div>
        {isShowDashBoard &&
          (isFetchingModels ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress
                color="secondary"
                style={{ width: 30, height: 30 }}
              />
            </div>
          ) : (
            <div className={classes.dashboardTableWrapper}>
              <EnhancedTable
                id="serverModels"
                isShowEmptyRows={false}
                localPaginationAbove={true}
                headCells={headCells}
                rows={dashboardModels || []}
                checkBoxTableCell={(id, name, index) => (
                  <TableCell padding="default">{index + 1}</TableCell>
                )}
                customBtns={(name, id, { type }) => {
                  const buttons = [
                    [t("backends.details"), () => fetchDetails(name), InfoOutlinedIcon],
                    ...(isRuntime ? [[ t("backends.check"), handleUpdateModel(name, type), CheckIcon, handleCheckSampleModalOpen ]] : []),
                    [t( isRuntime ? "backends.unload" : "common.delete"), handleUpdateModel(name, type), isRuntime ? ArrowDownwardIcon : DeleteIcon, handleConfirmModalOpen],
                  ];

                  return (
                    <div className={classes.dashboardButtonGroup}>
                      {buttons.map(([label, action, icon, addFunc], i) => (
                        <IconButton
                          key={i}
                          onClick={() => action(addFunc)}
                          disabled={!isRuntime && i == 0} //@TODO: remove this later
                          Icon={icon}
                          title={label}
                        />
                      ))}
                    </div>
                  );
                }}
              />
            </div>
          ))}
      </DialogContent>

      <DialogActions>
        <Button color="primary" autoFocus onClick={handleCancel}>
          {t("common.cancel")}
        </Button>
        <Button
          color="primary"
          disabled={isDisableButton}
          onClick={() => {
            const data = {
              ...backendData,
              url: backendData.url.trim(),
              adminUrl: backendData.adminUrl?.trim(),
            };

            onSave(subtractObject(data, initData), handleCancel);
          }}
        >
          {isClone ? t("common.clone") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
